import { Link } from '@tanstack/react-router'
import { MessageSquareText, Sparkles } from 'lucide-react'
import { useModerationReviews } from '../../lib/reviews'

/** Real negative-review AI-response automation (ReviewLowRatingConsumer, see
 * ai-engine/app/services/automation_ai.py's generate_review_response) — a rating < 3 review
 * gets an AI-drafted staff reply, which a manager edits/publishes from /reviews. This widget
 * surfaces how many are waiting for review right on the ops page, not just buried in the
 * reviews table. */
export default function ReviewAutomationWidget() {
  const { data, isLoading } = useModerationReviews()
  const reviews = data?.pages.flatMap((p) => p.items) ?? []

  const negative = reviews.filter((r) => r.rating < 3)
  const pendingDraft = negative.filter((r) => r.storeReplyDraft && !r.storeReply)
  const awaitingDraft = negative.filter((r) => !r.storeReplyDraft && !r.storeReply)
  const published = negative.filter((r) => r.storeReply)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <MessageSquareText className="w-5 h-5 text-blue-600" />
          <h2 className="font-semibold text-gray-800 dark:text-white">Review Response Automation</h2>
        </div>
        <Link to="/reviews" className="text-xs font-medium text-blue-600 hover:underline">
          Open moderation →
        </Link>
      </div>

      <div className="p-4">
        {isLoading ? (
          <div className="h-16 rounded-lg bg-gray-100 dark:bg-gray-700/40 animate-pulse" />
        ) : negative.length === 0 ? (
          <p className="text-sm text-gray-500">No negative reviews (rating &lt; 3) yet — nothing for the automation to draft.</p>
        ) : (
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-lg border border-purple-100 dark:border-purple-900/40 bg-purple-50 dark:bg-purple-900/10 p-3">
              <div className="flex items-center justify-center gap-1 text-purple-700 dark:text-purple-300">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="text-2xl font-bold">{pendingDraft.length}</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">AI draft ready</p>
            </div>
            <div className="rounded-lg border border-gray-100 dark:border-gray-700 p-3">
              <span className="text-2xl font-bold text-gray-700 dark:text-gray-200">{awaitingDraft.length}</span>
              <p className="mt-1 text-xs text-gray-500">Awaiting draft</p>
            </div>
            <div className="rounded-lg border border-gray-100 dark:border-gray-700 p-3">
              <span className="text-2xl font-bold text-gray-700 dark:text-gray-200">{published.length}</span>
              <p className="mt-1 text-xs text-gray-500">Published</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
